<script setup lang="ts">
import type { FilterInterval } from '~/models/Time'
import type { TaxonomyEntry } from '~/models/TaxonomyEntry'
import type { SubjectCode } from '~/models/Subject'
import type { Group } from '~/models/Group'
import type { GradeType } from '~/models/GradeType'
import type { GradeCode } from '~/models/Grade'
import { useI18n } from 'vue-i18n'
import { computed, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useQueries, useQuery } from '@tanstack/vue-query'
import { faArrowRight, faXmark } from '@fortawesome/duotone-regular-svg-icons'
import { sortBySubjectIndex } from '~/utils/subjectSorter'
import { findGradesByGradeType, sortByGradeIndex } from '~/utils/gradeSorter'
import { waitFor } from '~/utils/asyncUtils'
import useProductStore from '~/stores/product'
import useGroupsStore from '~/stores/groups'
import useAuthStore from '~/stores/auth'
import { PendoFeature } from '~/models/Pendo'
import { allGradeTypes as gradeTypeOptions } from '~/models/GradeType'
import { useUrlBuilder } from '~/composables/useUrlBuilder'
import useTime from '~/composables/useTime'
import { useStaticProduct } from '~/composables/useStaticProduct'
import { supportedSubjects } from '~/composables/useAssignTags'
import useContextMapper from '~/composables/contextMapper'
import useTaxonomyApi from '~/api/taxonomyApi'
import useAssignmentApi from '~/api/assignmentApi'
import Tooltip from '~/components/ui/Tooltip.vue'
import Select from '~/components/ui/Select.vue'
import Button from '~/components/ui/Button.vue'

const filterModel = defineModel<{
  groupIdValue: string | undefined
  gradeValue: GradeCode | undefined
  subjectValue: SubjectCode | undefined
  contextValue: string | undefined
  mode: string | undefined
  contentType: string | undefined
  gradeTypeValue: GradeType | undefined
  intervalValue: FilterInterval['name'] | undefined
}>()

const { t } = useI18n()
const groupsStore = useGroupsStore()
const { findGroupById } = groupsStore
const { sortedGroups: groupOptions, hasGroups, isLoading: isLoadingGroups } = storeToRefs(groupsStore)
const { selectedGradeTypeGrades: gradeOptions } = storeToRefs(useAuthStore())
const { filteredSubjectCodes } = storeToRefs(useProductStore())
const { getTaxonomyEntriesByParentContentId } = useTaxonomyApi()
const { getContextsBySubjectAndGrade } = useContextMapper()
const { filteredReportFilterIntervals } = useTime()
const { getReportAlternatives } = useAssignmentApi()
const { Terminprover } = useStaticProduct()
const { resolveBaseUrl } = useUrlBuilder()

const groupValue = ref<Group | undefined>()
const gradeValue = ref<GradeCode | undefined>()
const subjectValue = ref<SubjectCode | undefined>()
const contextValue = ref<TaxonomyEntry | undefined>()
const gradeTypeValue = ref<GradeType | undefined>()
const intervalValue = ref<FilterInterval['name'] | undefined>()

const isExamMode = computed(() => filterModel.value?.mode === 'exam')
const examParams = computed(() => ({ path: resolveBaseUrl(Terminprover) }))

const subjectOptions = computed(() => {
  if (isExamMode.value) {
    return filteredSubjectCodes.value.filter(code => supportedSubjects.includes(code))
  }
  return subjectAlternatives.value
})

const relevantContexts = computed((): number[] => {
  if (!subjectValue.value || !gradeTypeValue.value) {
    return []
  }

  return getContextsBySubjectAndGrade(
    [subjectValue.value],
    findGradesByGradeType(gradeTypeValue.value).sort(sortByGradeIndex)
  ) || []
})

const { data: alternatives } = useQuery({
  enabled: computed(() => !isExamMode.value),
  queryKey: ['report_alternatives'],
  queryFn: () => getReportAlternatives(),
  staleTime: Infinity,
  placeholderData: { subject: [], grade: [], context: [] },
})

const subjectAlternatives = computed(() => alternatives.value?.subject.toSorted(sortBySubjectIndex) ?? [])

const contextQueries = useQueries({
  queries: computed(() =>
    relevantContexts.value.map(id => ({
      queryKey: ['context_tag', id],
      queryFn: () => getTaxonomyEntriesByParentContentId(id),
      staleTime: Infinity,
      enabled: !!id,
      placeholderData: () => [],
    }))
  )
})

const contextFetched = computed(() => contextQueries.value.length > 0 && contextQueries.value.every(query => query.isFetched))

const contextOptions = computed<TaxonomyEntry[]>(() => {
  const all = contextQueries.value.flatMap(query => query.data ?? [])
  const seen = new Set<number | string>()
  return all.filter((entry) => {
    const key = entry.identifier
    if (seen.has(key))
      return false
    seen.add(key)
    return true
  })
})

const intervalOptions = computed<FilterInterval['name'][]>(() => filteredReportFilterIntervals.map(interval => interval.name))

const reportFilter = computed(() => {
  return {
    groupValue: groupValue.value?.groupId,
    gradeValue: gradeValue.value,
    subjectValue: subjectValue.value,
    contextValue: contextValue.value?.identifier,
    gradeTypeValue: gradeTypeValue.value,
    intervalValue: intervalValue.value,
  }
})

watch(reportFilter, () => {
  if (!filterModel.value) {
    return
  }
  if (filterModel.value.groupIdValue !== reportFilter.value.groupValue) {
    filterModel.value.groupIdValue = reportFilter.value.groupValue
  }
  if (filterModel.value.gradeValue !== reportFilter.value.gradeValue) {
    filterModel.value.gradeValue = reportFilter.value.gradeValue
  }
  if (filterModel.value.subjectValue !== reportFilter.value.subjectValue) {
    filterModel.value.subjectValue = reportFilter.value.subjectValue
  }
  if (filterModel.value.contextValue !== reportFilter.value.contextValue) {
    filterModel.value.contextValue = reportFilter.value.contextValue
  }
  if (filterModel.value.gradeTypeValue !== reportFilter.value.gradeTypeValue) {
    filterModel.value.gradeTypeValue = reportFilter.value.gradeTypeValue
  }
  if (filterModel.value.intervalValue !== reportFilter.value.intervalValue) {
    filterModel.value.intervalValue = reportFilter.value.intervalValue
  }
}, { deep: true })

watch(filterModel, async () => {
  if (!filterModel.value) {
    return
  }
  if (filterModel.value.groupIdValue !== groupValue.value?.groupId) {
    if (!hasGroups.value && isLoadingGroups.value) {
      await waitFor(() => isLoadingGroups.value === false, 2500)
    }
    groupValue.value = findGroupById(filterModel.value.groupIdValue ?? '')
  }
  if (filterModel.value.gradeValue !== gradeValue.value) {
    gradeValue.value = filterModel.value.gradeValue
  }
  if (filterModel.value.subjectValue !== subjectValue.value) {
    subjectValue.value = filterModel.value.subjectValue
  }
  if (filterModel.value.contextValue !== contextValue.value?.identifier) {
    const val = filterModel.value.contextValue
    if (contextOptions.value?.length === 0) {
      await waitFor(() => contextFetched.value, 2500)
    }
    contextValue.value = contextOptions.value?.find((context: TaxonomyEntry) => context.identifier === val)
  }
  if (filterModel.value.gradeTypeValue !== gradeTypeValue.value) {
    gradeTypeValue.value = filterModel.value.gradeTypeValue as GradeType
  }
  if (filterModel.value.intervalValue !== intervalValue.value) {
    intervalValue.value = filterModel.value.intervalValue
  }
}, { deep: true, immediate: true })
</script>

<template>
  <div class="max-w-screen-au mx-auto flex flex-col place-items-end space-y-5 px-4 py-6 sm:px-8 md:flex-row md:space-y-0 md:space-x-5">
    <!-- Group -->
    <label class="space-y-2">
      <span
        class="block text-base font-medium"
        v-text="t('report.toolbar.groupDropdown')"
      />
      <Select
        v-model="groupValue"
        :options="groupOptions"
        :placeholder="t('report.toolbar.groupDropdown')"
        :variant="!isExamMode ? 'outline' : 'solid'"
        :option-label="(group: Group) => group.name"
        :data-pendo="PendoFeature.Report.FilterGroup"
      />
    </label>
    <!-- Subject -->
    <label v-if="!isExamMode" class="space-y-2">
      <span
        class="block text-base font-medium"
        v-text="t('report.toolbar.subjectDropdown')"
      />
      <Select
        v-model="subjectValue"
        :options="subjectOptions"
        variant="outline"
        :placeholder="t('report.toolbar.subjectDropdown')"
        :option-label="(subject: SubjectCode) => t(`metadata.subjects.${subject}`)"
        :data-pendo="PendoFeature.Report.FilterSubject"
      />
    </label>
    <!-- Grade -->
    <label v-if="isExamMode" class="space-y-2">
      <span
        class="block text-base font-medium"
        v-text="t('report.toolbar.gradeDropdown')"
      />
      <Select
        v-model="gradeValue"
        :options="gradeOptions"
        :placeholder="t('report.toolbar.gradeDropdown')"
        :option-label="(grade: GradeCode) => t(`metadata.grades.${grade}`)"
        :data-pendo="PendoFeature.Report.FilterGrade"
      />
    </label>
    <!-- GradeType -->
    <label v-if="!isExamMode" class="space-y-2">
      <span
        class="block text-base font-medium"
        v-text="t('report.toolbar.gradeTypeDropdown')"
      />
      <Select
        v-model="gradeTypeValue"
        :options="gradeTypeOptions"
        :placeholder="t('report.toolbar.gradeTypeDropdown')"
        :option-label="(gradeType: GradeType) => t(`metadata.gradeTypes.short.${gradeType}`)"
        variant="outline"
        :data-pendo="PendoFeature.Report.FilterContext"
      />
    </label>
    <!-- Interval -->
    <label v-if="!isExamMode" class="space-y-2">
      <span
        class="block text-base font-medium"
        v-text="t('report.toolbar.intervalDropdown')"
      />
      <Select
        v-model="intervalValue"
        :options="intervalOptions"
        :placeholder="t('report.toolbar.intervalDropdown')"
        :option-label="(interval: FilterInterval['name']) => t(`report.interval.${interval}`)"
        variant="outline"
        :data-pendo="PendoFeature.Report.FilterContext"
      />
    </label>
    <!-- Context -->
    <label v-if="!isExamMode && contextOptions.length" class="space-y-2">
      <span
        class="block text-base font-medium"
        v-text="t('report.toolbar.contextDropdown')"
      />
      <Select
        v-model="contextValue"
        :options="contextOptions"
        :placeholder="t('report.toolbar.contextDropdown')"
        :option-label="(context: TaxonomyEntry) => context.name"
        :data-pendo="PendoFeature.Report.FilterContext"
        variant="outline"
      />
    </label>

    <div class="order-last ml-auto">
      <Tooltip
        v-if="isExamMode"
        :content="t('report.exam.tooltip.close')"
        side="top"
      >
        <Button
          :to="examParams"
          size="lg"
          shape="square"
          :aria-label="t('close')"
        >
          <font-awesome-icon :icon="faXmark" />
        </Button>
      </Tooltip>
      <Tooltip
        v-else
        :content="t('report.exam.tooltip.open')"
        side="top"
      >
        <Button
          :to="examParams"
          size="lg"
        >
          {{ t('report.exam.title') }}
          <font-awesome-icon :icon="faArrowRight" />
        </Button>
      </Tooltip>
    </div>
  </div>
</template>
